import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from './api.service';
import { PatientApiPayload } from '../utils/patient-form-mapper';

/** Plan search item from GET /patients/plans/search */
export interface PlanSearchItem {
  planId: number;
  planName?: string;
  insuranceName?: string;
  insuranceId?: number;
  planCoPay?: number;
  [key: string]: unknown;
}

export interface PlanSearchResponse {
  plans: PlanSearchItem[];
}

export type WcnfPlanType = 0 | 1 | 2;

export interface WcnfPlanTypeResponse {
  patientAccountNo: number;
  planId: number;
  planType: WcnfPlanType;
  wcCase: string;
}

export interface WcnfCaseItem {
  caseId: number;
  patientAccountNo: number;
  practiceId: number;
  providerId: number;
  planId: number;
  planType: WcnfPlanType;
  caseComments?: string;
  caseCompensationId?: number | null;
  caseNoFaultId?: number | null;
  enteredBy?: number | null;
  enteredDate?: string | null;
}

export interface ListWcnfCasesResponse {
  items: WcnfCaseItem[];
}

export interface GenerateWcnfCaseRequest {
  patientAccountNo: number;
  practiceId: number;
  providerId: number;
  planId: number;
  planType: WcnfPlanType;
  caseComments?: string;
}

export interface GenerateWcnfCaseResponse {
  case?: WcnfCaseItem;
}

export interface NoFaultItem {
  noFaultId: number;
  patientAccountNo?: number;
  planId?: number;
  caseId?: number;
  [key: string]: unknown;
}

export interface GetNoFaultResponse {
  item?: NoFaultItem;
}

export interface CompensationItem {
  compensationId: number;
  patientAccountNo?: number;
  planId?: number;
  caseId?: number;
  [key: string]: unknown;
}

export interface GetCompensationResponse {
  item?: CompensationItem;
}

/** Raw API response: plans live under data.plans */
export interface PlanSearchApiResponse {
  status?: string;
  message?: string;
  data?: { plans?: PlanSearchItem[] };
}

/** Comment category from GET /patients/comment-categories */
export interface CommentCategoryItem {
  commentId: number;
  commentMessage: string;
  commentColorCode?: number;
}

export interface CommentCategoriesResponse {
  commentCategories: CommentCategoryItem[];
}

/** Single ledger entry (Delphi tblLedger) – from GET /patients/ledger/{ledgerId} or list response. */
export interface LedgerEntryApi {
  ledgerId?: number;
  /** REST list rows often use `pkId` as the ledger primary key. */
  pkId?: number;
  ledgerAccountNo?: string;
  ledgerAmount?: number;
  ledgerDate?: string;
  /** Paid date from ledger list (ISO); preferred over `ledgerDate` for display / adjustments. */
  datePaid?: string | null;
  ledgerAppliedTo?: string;
  ledgerCardApprovalNo?: string;
  ledgerCardApprovedAmt?: number;
  ledgerCardExpiryDate?: string;
  ledgerChargeCoPay?: number;
  ledgerCheckNo?: string;
  ledgerComments?: string;
  /** Some GET /patients/ledger/{id} payloads use `comments` instead of `ledgerComments`. */
  comments?: string;
  ledgerCreditCardNo?: string;
  ledgerPaymentType?: number;
  ledgerPrintOnStatement?: boolean;
  ledgerRemitCode?: string;
  ledgerType?: number;
  locationId?: number;
  patientAccountNo?: number;
  patientVisitId?: number;
  paymentBatchAdminId?: number;
  practiceId?: number;
  visitChargeEntryId?: number;
  batchAdminId?: number;
  [key: string]: unknown;
}

/** Response from GET /patients/ledger/{ledgerId} */
export interface LedgerDetailApiResponse {
  ledger?: LedgerEntryApi;
  message?: string;
}

export interface UpdateLedgerRequest {
  ledgerDate?: string;
  ledgerAmount?: number;
  ledgerComments?: string;
  ledgerPaymentType?: number;
  ledgerCheckNo?: string;
  ledgerAppliedTo?: string;
  ledgerPrintOnStatement?: boolean;
  ledgerCreditCardNo?: string;
  ledgerCardApprovalNo?: string;
  ledgerCardApprovedAmt?: number;
  ledgerCardExpiryDate?: string;
  ledgerRemitCode?: string;
}

/** Summary totals from GET /patients/ledger (data.summary). */
export interface PatientLedgerSummaryApi {
  planAmt?: number;
  patientAmt?: number;
  planPayment?: number;
  copayment?: number;
  patientPayment?: number;
  writeOff?: number;
  advancePayment?: number;
  discount?: number;
  cash?: number;
  creditMemo?: number;
  check?: number;
  creditCard?: number;
}

/** Response from GET /patients/ledger (list). Backend returns { data: { entries: [], summary: {} } }. */
export interface PatientLedgerListApiResponse {
  status?: string;
  message?: string;
  data?: {
    message?: string;
    entries?: LedgerEntryApi[] | null;
    summary?: PatientLedgerSummaryApi;
  };
  ledger?: LedgerEntryApi[];
}

/** Params for getPatientLedgerList (no index signature so dot access is allowed). */
export interface GetPatientLedgerListParams {
  patientAccountNo: number | string;
  patientVisitId?: number | string | null;
  dosFrom?: string;
  dosTo?: string;
  includeCharges?: boolean;
  includeLedger?: boolean;
  includeSubmit?: boolean;
  includeStatement?: boolean;
}

/** Single eligibility record from GET /patients/eligibility (data array item). */
export interface EligibilityRecordApi {
  eligibilityId?: number;
  patientAccountNo?: number | string;
  patientName?: string;
  patientInsuredPartyId?: number;
  planId?: number;
  planName?: string;
  serviceTypeCode?: string;
  serviceDate?: string;
  eligibilityDate?: string;
  coPayAmount?: number;
  deductibleAmount?: number;
  response?: string;
  traceNumber?: string;
  responseString?: string;
  practice?: string;
  location?: string;
  [key: string]: unknown;
}

/** Response from GET /patients/eligibility */
export interface ListEligibilityResponse {
  data?: EligibilityRecordApi[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/** Query params for GET /patients/eligibility (all optional). */
export interface GetEligibilityListParams {
  patientAccountNo?: number | string;
  patientName?: string;
  practice?: string;
  location?: string;
  serviceDateFrom?: string;
  serviceDateTo?: string;
  eligibilityDateFrom?: string;
  eligibilityDateTo?: string;
  serviceTypeCode?: string;
  planName?: string;
  response?: string;
  page?: number;
  limit?: number;
}

/** Request body for POST /patients/eligibility (Add Eligibility). */
export interface AddEligibilityRequest {
  patientAccountNo: number;
  patientInsuredPartyId: number;
  serviceTypeCode: string;
  serviceDate: string;
  eligibilityDate: string;
  coPayAmount?: number;
  deductibleAmount?: number;
  response?: string;
  traceNumber?: string;
  responseString?: string;
}

/** Response from POST /patients/eligibility */
export interface AddEligibilityResponse {
  eligibilityId?: number;
  message?: string;
}

export interface EligibilityContextRequest {
  patientAccountNo: number;
  serviceDate?: string;
}

export interface EligibilityContextServiceTypeCode {
  code?: string | number;
  name?: string;
}

export interface EligibilityContextBenefit {
  result?: string;
  coverage?: string;
  quantity?: string | number;
  inplanNetwork?: string;
  description?: string;
  levelCode?: string;
  levelDescription?: string;
  periodCode?: string;
  periodDescription?: string;
  notes?: string;
  coverageCode?: string;
  amount?: string | number;
}

export interface EligibilityContextData {
  patientFullName?: string;
  practice?: { shortName?: string; [key: string]: unknown };
  location?: { shortName?: string; [key: string]: unknown };
  serviceDate?: string;
  eligibilityDate?: string;
  plan?: { planName?: string; [key: string]: unknown };
  copay?: string | number;
  deductible?: string | number;
  serviceTypeCodes?: EligibilityContextServiceTypeCode[];
  serviceTypeCode?: string | number;
  eligibility?: {
    status?: string;
    result?: string;
    serviceTypeCode?: string | number;
    benefits?: EligibilityContextBenefit[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface EligibilityContextApiResponse {
  success?: boolean;
  message?: string;
  data?: EligibilityContextData;
  errors?: Record<string, string[] | string>;
}

export interface EligibilityScreenDetailsRequest {
  patientAccountNo: number;
  eligibilityId: number;
}

export interface EligibilityScreenDetailsPayer {
  line1?: string;
  idLabel?: string;
  idValue?: string;
}

export interface EligibilityScreenDetailsProvider {
  line1?: string;
  npi?: string;
}

export interface EligibilityScreenDetailsSubscriber {
  line1?: string;
  memberIdLabel?: string;
  memberId?: string;
}

export interface EligibilityScreenDetailsDependent {
  line1?: string;
  memberIdLabel?: string;
  memberId?: string;
}

export interface EligibilityScreenDetailsValidation {
  level?: string;
  detailText?: string;
  aaa?: Record<string, string>;
}

export interface EligibilityBenefitItem {
  eb01: string;
  eb02: string;
  eb03: string;
  eb04: string;
  eb05: string;
  eb06: string;
  eb07: string;
  eb08: string;
  eb09: string;
  eb10: string;
  eb11: string;
  eb12: string;
  eb13: string;
  dateOfLastUpdate?: string;
  datePeriods?: Array<{
    dtp01?: string;
    dtp02?: string;
    dtp03?: string;
    date?: string;
    dtp01Code?: string;
    dtp02Code?: string;
  }>;
  messages?: string[];
}

export interface EligibilityScreenDetailsData {
  accountNumber?: number | string;
  eligibilityId?: number;
  serviceDate?: string;
  eligibilityDate?: string;
  serviceTypeCode?: string | number;
  plan?: { planId?: number; planName?: string };
  copay?: string | number;
  deductible?: string | number;
  resultStatusText?: string;
  payer?: EligibilityScreenDetailsPayer;
  provider?: EligibilityScreenDetailsProvider;
  subscriber?: EligibilityScreenDetailsSubscriber;
  dependents?: EligibilityScreenDetailsDependent[];
  validations?: EligibilityScreenDetailsValidation[];
  eligibilityBenefits?: EligibilityBenefitItem[];
  messageList?: string[];
  rawEligibilityResponseStringPresent?: boolean;
}

export interface EligibilityScreenDetailsApiResponse {
  status?: string;
  message?: string;
  data?: EligibilityScreenDetailsData;
}

/** Single consent item from GET /api/v1/patients/{patientAccountNo}/consents (supports camelCase or snake_case from API). */
export interface PatientConsentListItem {
  practiceName?: string;
  practiceDescription?: string;
  name?: string;
  address?: string;
  visitNo?: number | string;
  enteredBy?: string;
  enteredDate?: string;
  practice_name?: string;
  practice_description?: string;
  visit_no?: number | string;
  entered_by?: string;
  entered_date?: string;
  [key: string]: unknown;
}

/** Response from GET /api/v1/patients/{patientAccountNo}/consents (array or { data: [] }). */
export type PatientConsentListResponse = PatientConsentListItem[] | { data?: PatientConsentListItem[]; message?: string };

/** One entry from GraphQL getPatientLedger.entries */
export interface GetPatientLedgerEntryGql {
  pkId?: number;
  /** When present, REST GET /patients/ledger/{ledgerId} key for Edit Ledger (same as Payment screen). */
  ledgerId?: number | null;
  patientVisitId?: number;
  visitChargeEntryId?: number;
  patientDate?: string;
  dosDate?: string;
  entryDate?: string;
  datePaid?: string | null;
  planName?: string;
  description?: string;
  fee?: number;
  planAmt?: number;
  patientAmt?: number;
  entryType?: string;
  userName?: string;
  /** Optional extra fields used by Patient Ledger UI (may or may not be present in GraphQL response). */
  planBal?: number;
  patientBal?: number;
  comments?: string;
  planDescription?: string;
  provider?: string;
  location?: string;
  ledgerType?: string | number;
  chargeIsTransferred?: boolean;
  claimStatus?: number | string;
  statementId?: number;
  rowColorKey?: string;
  rowColorHex?: string;
  nextAction?: string;
}

/** Result of GraphQL getPatientLedger (message, entries, summary). */
export interface GetPatientLedgerGqlResult {
  message?: string;
  entries?: GetPatientLedgerEntryGql[] | null;
  summary?: PatientLedgerSummaryApi;
}

/** Chart data: medication item from GET /patients/{id}/chart-data */
export interface ChartMedicationItem {
  patientMedicationId: number;
  drugShortName: string;
  drugStrength?: string;
  patientMedicationDoseType?: string;
}

/** Chart data: allergy item */
export interface ChartAllergyItem {
  patientAllergiesId: number;
  allergenId: number;
  allergenName: string;
}

/** Chart data: problem list item */
export interface ChartProblemListItem {
  templateVisitId: number;
  problemShortDesc: string;
  templateVisitDate?: string;
}

/** Response from GET /patients/{patientAccountNo}/chart-data */
export interface PatientChartDataResponse {
  patientAccountNo: number;
  type?: string;
  medication?: { items: ChartMedicationItem[] };
  allergies?: { hasNone?: boolean; items: ChartAllergyItem[] };
  problemList?: { items: ChartProblemListItem[] };
}

/**
 * API response for GET /patients/{patientAccountNo}/insured-party/{patientInsuredPartyId}/plan-extra-info
 * Backend may use camelCase or snake_case; mapper in modal handles both.
 */
export interface PlanExtraInfoApiResponse {
  wcCaseNo?: string | null;
  injuryAccident?: string | null;
  injuryDate?: string | null;
  accidentHour?: string | null;
  admissionDate?: string | null;
  admission?: string | null;
  similarFirstIllness?: string | null;
  dischargeDate?: string | null;
  discharge?: string | null;
  initialTreatment?: string | null;
  lastEligibilityDate?: string | null;
  possibleDisability?: boolean | string | null;
  phcp?: boolean | string | null;
  coPayBypass?: string | null;
  unableToWorkFrom?: string | null;
  unableToWorkTo?: string | null;
  reservedField19Hcfa1500?: string | null;
  reservedField19HCFA1500?: string | null;
  accidentType?: string | null;
  accidentState?: string | null;
  injurySite?: string | null;
  lmp?: string | null;
  illnessFirstSymptoms?: string | null;
  lastSeenDate?: string | null;
  lastSeen?: string | null;
  eligibility?: string | null;
  planType?: string | null;
  familyPlanning?: boolean | string | null;
  patientStatus?: string | null;
  abortionCode?: string | null;
  accidentCode?: string | null;
  laboratory?: string | null;
  emg?: string | null;
  lastStatementDate?: string | null;
  amountDue?: string | null;
  numberOfStatements?: string | null;
  [key: string]: any;
}

/**
 * PUT /patients/{id}/insured-party/{partyId}/plan-extra-info – request body (matches Swagger).
 */
export interface PlanExtraInfoUpdateRequest {
  abortionCode: string;
  accidentCode: string;
  accidentHour: number;
  accidentState: string;
  accidentType: number;
  admission: string | object;
  coPayByPass: string;
  discharge: string | object;
  eligibility: number;
  emg: boolean;
  familyPlanning: boolean;
  illnessFirstSymptoms: string | object;
  initialTreatment: string | object;
  injuryAccident: string | object;
  injurySite: string;
  laboratory: number;
  lastEligibilityDate: string | object;
  lastSeen: string | object;
  lmp: string | object;
  patientStatus: number;
  phcp: boolean;
  possibleDisability: boolean;
  reservedField19HCFA1500: string;
  similarFirstIllness: string | object;
  unableToWorkFrom: string | object;
  unableToWorkTo: string | object;
  wcCase: string;
}

/** Option item for dropdowns: API may return { value, label } or { id, name }. */
export interface PlanExtraInfoDropdownOption {
  value?: string | number;
  label?: string;
  id?: string | number;
  name?: string;
  [key: string]: any;
}

/** Response from GET /patients/plan-extra-info-dropdowns */
export interface PlanExtraInfoDropdownsResponse {
  accidentHour?: PlanExtraInfoDropdownOption[];
  accidentType?: PlanExtraInfoDropdownOption[];
  eligibility?: PlanExtraInfoDropdownOption[];
  patientStatus?: PlanExtraInfoDropdownOption[];
  laboratory?: PlanExtraInfoDropdownOption[];
  [key: string]: PlanExtraInfoDropdownOption[] | undefined;
}

/** Request body for PATCH /patients/{patientAccountNo} when updating only attached documents */
export interface UpdatePatientAttachedDocumentsPayload {
  attachedDocuments: Array<{
    documentName: string;
    documentTypeId: number;
    documentUrl: string;
  }>;
}

/** One document item for PATCH (camelCase). Backend may expect snake_case – use buildAttachedDocumentsPayload. */
export interface AttachedDocumentItem {
  documentName: string;
  documentTypeId: number;
  documentUrl: string;
}

/**
 * Build PATCH body for attached documents so backend receives valid payload.
 * Some backends expect snake_case (document_name, document_type_id, document_url).
 * Use useSnakeCase: true if your API validates/serializes with snake_case.
 */
export function buildAttachedDocumentsPayload(
  items: AttachedDocumentItem[],
  useSnakeCase = false
): { attachedDocuments: any[] } {
  const valid = items.filter(
    (x) =>
      typeof x.documentUrl === 'string' &&
      x.documentUrl.trim() !== '' &&
      typeof x.documentTypeId === 'number' &&
      !isNaN(x.documentTypeId) &&
      x.documentTypeId > 0
  );
  const attachedDocuments = valid.map((x) => {
    const name = typeof x.documentName === 'string' && x.documentName.trim() !== '' ? x.documentName.trim() : 'document';
    if (useSnakeCase) {
      return {
        document_name: name,
        document_type_id: x.documentTypeId,
        document_url: x.documentUrl.trim()
      };
    }
    return {
      documentName: name,
      documentTypeId: x.documentTypeId,
      documentUrl: x.documentUrl.trim()
    };
  });
  return { attachedDocuments };
}

export interface PatientFormData {
  // Demographic
  ssn?: string;
  chartNo?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  middleInitial?: string;
  dateOfBirth?: string;
  age?: string;
  maritalStatus?: string;
  sex?: string;
  isActive?: boolean;
  plan?: string;
  location?: number;
  provider?: number;
  practice?: number;

  // General Info
  email?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  homeTel?: string;
  workTel?: string;
  extension?: string;
  cell?: string;
  primaryContact?: string;
  comments?: string;
  race?: string;
  ethnicity?: string;
  language?: string;
  occupationType?: string;

  // Recall
  nextVisitDate?: string;
  recallReason?: string;

  // Insured Party
  insPlan?: string;
  insIsActive?: boolean;
  insPolicyNo?: string;
  insGroupNo?: string;
  insCertNo?: string;
  insUnionNo?: string;
  insDateSigned?: string;
  insExpirationDate?: string;
  insCopay?: string;
  insRelation?: string;
  insFirstName?: string;
  insLastName?: string;
  insSsn?: string;
  insDob?: string;
  insAddress1?: string;
  insAddress2?: string;
  insCity?: string;
  insState?: string;
  insZip?: string;
  insHomeTel?: string;
  insWorkTel?: string;
  insSex?: string;
  insEffectiveDate?: string;
  insAttachWith?: string;
  insPriority?: string;
  insCoverageType?: string;

  // Responsible / Emergency Party
  resFirstName?: string;
  resLastName?: string;
  resSsn?: string;
  resDob?: string;
  resSex?: string;
  resEmployer?: string;
  resRelation?: string;
  resAddress1?: string;
  resAddress2?: string;
  resCity?: string;
  resState?: string;
  resZip?: string;
  resHomeTel?: string;
  resWorkTel?: string;

  // School
  schoolName?: string;
  schoolAddress1?: string;
  schoolAddress2?: string;
  schoolCity?: string;
  schoolState?: string;
  schoolZip?: string;
  schoolPhone1?: string;
  schoolExt1?: string;
  schoolPhone2?: string;
  schoolExt2?: string;
  schoolFax?: string;
  schoolEmail?: string;
  schoolWebsite?: string;

  // Lawyer
  lawyerName?: string;
  lawyerFirm?: string;
  lawyerAddress1?: string;
  lawyerAddress2?: string;
  lawyerCity?: string;
  lawyerState?: string;
  lawyerZip?: string;
  lawyerPhone1?: string;
  lawyerExt1?: string;
  lawyerPhone2?: string;
  lawyerExt2?: string;
  lawyerFax?: string;
  lawyerEmail?: string;
  lawyerWebsite?: string;
  lawyerComments?: string;

  // Employer
  employerName?: string;
  employerAddress1?: string;
  employerAddress2?: string;
  employerCity?: string;
  employerState?: string;
  employerZip?: string;
  employerPhone1?: string;
  employerExt1?: string;
  employerPhone2?: string;
  employerExt2?: string;
  employerFax?: string;
  employerEmail?: string;
  employerWebsite?: string;
  employerComments?: string;

  // Precertification
  precertNumber?: string;
  precertFromDate?: string;
  precertToDate?: string;
  precertComments?: string;
}

export interface PatientResponse {
  status: string;
  message: string;
  data: {
    message?: string;
    success?: boolean;
    patient?: {
      patientAccountNo?: number;
      [key: string]: any;
    };
    id?: number;
    [key: string]: any;
  };
}

export interface PatientData extends PatientApiPayload {
  id?: number;
}

export interface ListPatientsInput {
  patientAccountNo?: number | string;
  patientFirstName?: string;
  patientLastName?: string;
  patientSSN?: string;
  patientChartNo?: string;
  patientDOB?: string;
  practiceId?: number;
  locationId?: number;
  providerId?: number;
  planId?: number;
  isActive?: boolean;
  hasComments?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface InsuredPartyItem {
  patientInsuredPartyId?: number | string;
  planId?: number | string;
  planName?: string;
  insuranceName?: string;
  partyFirstNameofInsured?: string;
  partyLastNameofInsured?: string;
  partyCertificateOrMemberNo?: string;
  partyPlanCoPay?: number | string;
  partyIsActive?: boolean;
}

export interface PatientListItem {
  patientAccountNo: number | string;
  patientFirstName: string;
  patientLastName: string;
  patientMiddleName?: string;
  patientSSN?: string;
  patientChartNo?: string;
  patientDOB?: string;
  patientAddress1?: string;
  patientAddress2?: string;
  patientCity?: string;
  patientState?: string;
  patientZip?: string;
  patientEmailID?: string;
  patientHomePhone?: string;
  patientCellNo?: string;
  practiceId?: number | string;
  practiceName?: string;
  locationId?: number | string;
  locationName?: string;
  providerId?: number | string;
  providerName?: string;
  planId?: number | string;
  planName?: string;
  isActive?: boolean;
  parentPatientAccountNo?: number | string;
  /** Comment/status color id (matches the `value` field from GET /patients/comment-categories). */
  patientStatusColor?: number | string | null;
  eLabPatientId?: string | null;
  insuredParty?: InsuredPartyItem[];
}

export interface ListPatientsResponse {
  message: string;
  total: number | string;
  page: number | string;
  limit: number | string;
  totalPages: number | string;
  data: PatientListItem[];
}

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private patientSummaryCache = new Map<number, Observable<any>>();
  constructor(private apiService: ApiService) { }

  /**
   * Create a new patient
   * Accepts API payload format
   */
  createPatient(patientData: PatientApiPayload): Observable<PatientResponse> {
    return this.apiService.post<PatientResponse>('patients', patientData);
  }

  /**
   * Update an existing patient (PATCH /patients/{patientAccountNo})
   * @param patientAccountNo - Required. Patient account number (path parameter).
   * @param payload - Request body. Can be full patient payload or only { attachedDocuments } for documents-only update.
   */
  updatePatient(patientAccountNo: number, payload: PatientApiPayload | UpdatePatientAttachedDocumentsPayload | Record<string, unknown>): Observable<PatientResponse> {
    return this.apiService.patch<PatientResponse>(`patients/${patientAccountNo}`, payload);
  }

  /**
   * Delete a patient
   * @param patientAccountNo - Patient Account Number
   * @returns Observable with delete response
   */
  deletePatient(patientAccountNo: number): Observable<any> {
    return this.apiService.delete<any>(`patients/${patientAccountNo}`).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle different error status codes
        let errorMessage = 'An error occurred while deleting the patient';

        if (error.status === 400) {
          errorMessage = error.error?.error?.message || 'Invalid request. Please check the patient account number.';
        } else if (error.status === 401) {
          errorMessage = 'Unauthorized. Please log in again.';
        } else if (error.status === 404) {
          errorMessage = error.error?.error?.message || 'Patient not found.';
        } else if (error.status === 500) {
          errorMessage = error.error?.error?.message || 'Internal server error. Please try again later.';
        } else if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        // Return error with status code for component to handle
        return throwError(() => ({
          status: error.status,
          message: errorMessage,
          error: error.error
        }));
      })
    );
  }

  /**
   * Get patient by Account Number
   * Returns data in API payload format
   */
  /**
   * GET /patients/{patientAccountNo}/chart-data
   * Medication, allergies, problem list. Use type=all (default), medication, allergies, or problem_list.
   */
  getPatientChartData(patientAccountNo: number | string, type: string = 'all'): Observable<PatientChartDataResponse> {
    const params = type !== 'all' ? { type } : undefined;
    return this.apiService.get<PatientChartDataResponse>(
      `patients/${patientAccountNo}/chart-data`,
      params ? { params: { type } } : undefined
    );
  }

  getPatientByAccountNo(patientAccountNo: number | string): Observable<PatientData> {
    return this.apiService.get<PatientData>(`patients/${patientAccountNo}`);
  }

  /**
   * GET /patients/{patientAccountNo}/documents/{documentId}
   * Returns document as blob (PDF or image). Backend must send CORS headers
   * (Access-Control-Allow-Origin, etc.) so the response can be read and displayed in the iframe.
   */
  getPatientDocument(patientAccountNo: number, documentId: number): Observable<Blob> {
    return this.apiService.get<Blob>(
      `patients/${patientAccountNo}/documents/${documentId}`,
      {
        responseType: 'blob',
        headers: { Accept: 'application/pdf, image/*, image/bmp, application/octet-stream' }
      }
    );
  }

  /**
   * DELETE /patients/{patientAccountNo}/documents/{documentId}
   * Permanently removes a stored patient document.
   */
  deletePatientDocument(
    patientAccountNo: number,
    documentId: number
  ): Observable<{ status?: string; message?: string; data?: { patientAccountNo?: number; documentId?: number } }> {
    return this.apiService.delete(
      `patients/${patientAccountNo}/documents/${documentId}`
    );
  }

  /**
   * GET /patients/{patientAccountNo}/education-documents/{documentId}
   * Returns education document as blob (application/octet-stream).
   * documentId is EducationLibraryID from getPatientEducation API.
   */
  getPatientEducationDocument(patientAccountNo: number, documentId: number): Observable<Blob> {
    return this.apiService.get<Blob>(
      `patients/${patientAccountNo}/education-documents/${documentId}`,
      {
        responseType: 'blob',
        headers: { Accept: 'application/pdf, image/*, application/octet-stream' }
      }
    );
  }

  /**
   * Get patient by ID (deprecated - use getPatientByAccountNo instead)
   * Returns data in API payload format
   */
  getPatientById(patientId: number): Observable<PatientData> {
    return this.getPatientByAccountNo(patientId);
  }

  /**
   * GET /api/v1/patients/{patientAccountNo}/last-visit
   * When withChargesOnly is true, matches Delphi ChargeDetailsDM.FindVisitID (Charge Entry Last Visit icon).
   */
  getLastVisit(patientAccountNo: number, withChargesOnly = false): Observable<any> {
    if (!patientAccountNo || isNaN(patientAccountNo)) {
      return throwError(() => new Error('Invalid patient account number.'));
    }
    const query = withChargesOnly ? '?withChargesOnly=true' : '';
    return this.apiService.get<any>(`patients/${patientAccountNo}/last-visit${query}`).pipe(
      map((res) => res?.data ?? res)
    );
  }

  /**
   * Get patient extra info by Account Number
   */
  getPatientExtraInfo(patientAccountNo: number | string): Observable<any> {
    return this.apiService.get<any>(`patients/${patientAccountNo}/extra-info`);
  }

  /**
   * GET /api/v1/patients/{patientAccountNo}/consents
   * Returns list of consent records for the patient (for Consent For Procedure modal).
   */
  getPatientConsents(patientAccountNo: number | string): Observable<PatientConsentListItem[]> {
    return this.apiService.get<PatientConsentListResponse>(`patients/${patientAccountNo}/consents`).pipe(
      map((res) => {
        if (Array.isArray(res)) return res as PatientConsentListItem[];
        if (res?.data && Array.isArray(res.data)) return res.data;
        return [];
      }),
      catchError((err: HttpErrorResponse) => {
        const message = err?.error?.message || err?.message || err?.statusText || 'Failed to load consents';
        return throwError(() => ({ status: err?.status, message }));
      })
    );
  }

  /**
   * DELETE /patients/{patientAccountNo}/insured-party/{patientInsuredPartyId}
   * Delete an insured party from patient details.
   */
  deleteInsuredParty(patientAccountNo: number | string, patientInsuredPartyId: number | string): Observable<{ message?: string; status?: string; data?: string }> {
    return this.apiService.delete<{ message?: string; status?: string; data?: string }>(
      `patients/${patientAccountNo}/insured-party/${patientInsuredPartyId}`
    );
  }

  /**
   * PUT /patients/{patientAccountNo}/insured-party/{patientInsuredPartyId}
   * Replace/update an existing insured party / plan. Body matches nested `insuredParty` fields; party id is only in the URL.
   */
  updateInsuredParty(
    patientAccountNo: number | string,
    patientInsuredPartyId: number | string,
    body: Record<string, unknown>
  ): Observable<PatientResponse> {
    return this.apiService.put<PatientResponse>(
      `patients/${patientAccountNo}/insured-party/${patientInsuredPartyId}`,
      body
    );
  }

  /**
   * GET /patients/{patientAccountNo}/insured-party/{patientInsuredPartyId}/plan-extra-info
   * Fetch Patient Plan Extra Information (HCFA 1500 fields) for the selected insured party.
   */
  getPlanExtraInfo(patientAccountNo: number | string, patientInsuredPartyId: number | string): Observable<PlanExtraInfoApiResponse> {
    return this.apiService.get<PlanExtraInfoApiResponse>(
      `patients/${patientAccountNo}/insured-party/${patientInsuredPartyId}/plan-extra-info`
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        const status = err?.status;
        const message = err?.error?.message || err?.message || err?.statusText || 'Failed to load plan extra info';
        return throwError(() => ({ status, message }));
      })
    );
  }

  /**
   * PUT /patients/{patientAccountNo}/insured-party/{patientInsuredPartyId}/plan-extra-info
   * Create or update Patient Plan Extra Information (HCFA 1500 fields). Request body matches Swagger PlanExtraInfoUpdateRequest.
   */
  updatePlanExtraInfo(
    patientAccountNo: number | string,
    patientInsuredPartyId: number | string,
    body: PlanExtraInfoUpdateRequest
  ): Observable<PlanExtraInfoApiResponse> {
    return this.apiService.put<PlanExtraInfoApiResponse>(
      `patients/${patientAccountNo}/insured-party/${patientInsuredPartyId}/plan-extra-info`,
      body
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        const status = err?.status;
        const message = err?.error?.message || err?.message || err?.statusText || 'Failed to save plan extra info';
        return throwError(() => ({ status, message }));
      })
    );
  }

  /**
   * Update patient extra info
   */
  updatePatientExtraInfo(patientAccountNo: number | string, data: any): Observable<any> {
    return this.apiService.put(`patients/${patientAccountNo}/extra-info`, data);
  }

  /**
   * GET /patients/plan-extra-info-dropdowns
   * Optional query: ?dropdown=accidentHour,eligibility to fetch specific dropdowns.
   */
  getPlanExtraInfoDropdowns(dropdowns?: string[]): Observable<PlanExtraInfoDropdownsResponse> {
    const params: { [key: string]: string } = {};
    if (dropdowns && dropdowns.length > 0) {
      params['dropdown'] = dropdowns.join(',');
    }
    return this.apiService.get<PlanExtraInfoDropdownsResponse>('patients/plan-extra-info-dropdowns', { params }).pipe(
      catchError((err: HttpErrorResponse) => {
        const status = err?.status;
        const message = err?.error?.message || err?.message || err?.statusText || 'Failed to load dropdown options';
        return throwError(() => ({ status, message }));
      })
    );
  }

  /**
   * List patients using GraphQL
   */
  listPatients(input: ListPatientsInput): Observable<{ listPatients: ListPatientsResponse }> {
    const query = `
      query ListPatients($input: ListPatientsInput!) {
        listPatients(input: $input) {
          message
          total
          page
          limit
          totalPages
          data {
            patientAccountNo
            patientFirstName
            patientLastName
            patientMiddleName
            patientSSN
            patientChartNo
            patientDOB
            patientAddress1
            patientAddress2
            patientCity
            patientState
            patientZip
            patientEmailID
            patientHomePhone
            patientCellNo
            practiceId
            practiceName
            locationId
            locationName
            providerId
            providerName
            planId
            planName
            isActive
            parentPatientAccountNo
            patientStatusColor
            eLabPatientId
            insuredParty {
              patientInsuredPartyId
              planId
              planName
              insuranceName
              partyFirstNameofInsured
              partyLastNameofInsured
              partyCertificateOrMemberNo
              partyPlanCoPay
              partyIsActive
            }
          }
        }
      }
    `;

    const variables = {
      input: {
        patientAccountNo: input.patientAccountNo,
        patientFirstName: input.patientFirstName,
        patientLastName: input.patientLastName,
        patientSSN: input.patientSSN,
        patientChartNo: input.patientChartNo,
        patientDOB: input.patientDOB,
        practiceId: input.practiceId,
        locationId: input.locationId,
        providerId: input.providerId,
        planId: input.planId,
        isActive: input.isActive,
        hasComments: input.hasComments,
        search: input.search,
        page: typeof input.page === 'number' && input.page >= 1 ? input.page : 1,
        limit: typeof input.limit === 'number' && input.limit > 0 ? input.limit : (parseInt(String(input.limit), 10) || 20)
      }
    };

    return this.apiService.graphql<{ listPatients: ListPatientsResponse }>(query, variables);
  }

  /**
   * Single patient row from GraphQL `listPatients` — same data as Patient list `_rawListPatient` (used for DoseSpot EMR).
   */
  getListPatientItemForEmr(patientAccountNo: number | string): Observable<PatientListItem | null> {
    const account =
      typeof patientAccountNo === 'string' ? parseInt(String(patientAccountNo).trim(), 10) : patientAccountNo;
    if (!account || isNaN(account) || account <= 0) {
      return of(null);
    }
    return this.listPatients({ patientAccountNo: account, page: 1, limit: 1 }).pipe(
      map((res) => {
        const rows = res?.listPatients?.data;
        return rows && rows.length > 0 ? rows[0] : null;
      })
    );
  }

  /**
   * Search plans by plan name or insurance name.
   * GET /patients/plans/search?q={searchText}&limit=20
   * API returns { status, message, data: { plans } } - we normalize to { plans }.
   * Returns empty plans on error so UI does not crash (401, 500, etc.).
   */
  searchPlans(query: string): Observable<PlanSearchResponse> {
    return this.apiService.get<PlanSearchApiResponse>('patients/plans/search', {
      params: { q: query ?? '', limit: 20 }
    }).pipe(
      map((res) => ({ plans: res?.data?.plans ?? [] })),
      catchError(() => of({ plans: [] }))
    );
  }

  getWcnfPlanType(patientAccountNo: number, planId: number): Observable<WcnfPlanTypeResponse> {
    return this.apiService.get<any>('patients/wcnf/plan-type', {
      params: { patientAccountNo, planId }
    }).pipe(
      map((res: any) => {
        const payload = res?.data != null ? res.data : res;
        return {
          patientAccountNo: Number(payload?.patientAccountNo ?? patientAccountNo),
          planId: Number(payload?.planId ?? planId),
          planType: Number(payload?.planType ?? 0) as WcnfPlanType,
          wcCase: String(payload?.wcCase ?? '')
        } as WcnfPlanTypeResponse;
      })
    );
  }

  listWcnfCases(
    patientAccountNo: number,
    practiceId: number,
    providerId: number,
    planId: number
  ): Observable<ListWcnfCasesResponse> {
    return this.apiService.get<any>('patients/wcnf/cases', {
      params: { patientAccountNo, practiceId, providerId, planId }
    }).pipe(
      map((res: any) => {
        const payload = res?.data != null ? res.data : res;
        return {
          items: Array.isArray(payload?.items) ? payload.items : []
        } as ListWcnfCasesResponse;
      })
    );
  }

  generateWcnfCase(body: GenerateWcnfCaseRequest): Observable<GenerateWcnfCaseResponse> {
    return this.apiService.post<any>('patients/wcnf/cases/generate', body).pipe(
      map((res: any) => {
        const payload = res?.data != null ? res.data : res;
        return { case: payload?.case } as GenerateWcnfCaseResponse;
      })
    );
  }

  getNoFault(noFaultId: number): Observable<GetNoFaultResponse> {
    return this.apiService.get<any>(`patients/wcnf/nofault/${noFaultId}`).pipe(
      map((res: any) => {
        const payload = res?.data != null ? res.data : res;
        return { item: payload?.item ?? payload } as GetNoFaultResponse;
      })
    );
  }

  updateNoFault(noFaultId: number, body: Record<string, unknown>): Observable<any> {
    return this.apiService.put<any>(`patients/wcnf/nofault/${noFaultId}`, body);
  }

  getCompensation(compensationId: number): Observable<GetCompensationResponse> {
    return this.apiService.get<any>(`patients/wcnf/compensation/${compensationId}`).pipe(
      map((res: any) => {
        const payload = res?.data != null ? res.data : res;
        return { item: payload?.item ?? payload } as GetCompensationResponse;
      })
    );
  }

  updateCompensation(compensationId: number, body: Record<string, unknown>): Observable<any> {
    return this.apiService.put<any>(`patients/wcnf/compensation/${compensationId}`, body);
  }

  /**
   * Get comment categories for patient General Info (comment/color dropdown).
   * GET /patients/comment-categories
   */
  getCommentCategories(): Observable<CommentCategoriesResponse> {
    return this.apiService.get<any>('patients/comment-categories').pipe(
      map((res: any) => ({
        commentCategories: res?.data?.commentCategories ?? res?.commentCategories ?? []
      })),
      catchError(() => of({ commentCategories: [] }))
    );
  }

  /**
   * Search patients by fullName
   * GET /patients/search?practiceId={practiceId}&locationId={locationId}&providerId={providerId}&fullName={fullName}
   */
  searchPatients(params: {
    practiceId: number;
    locationId: number;
    providerId: number;
    fullName?: string;
    dob?: string; // Date of Birth in yyyy-mm-dd format
  }): Observable<any> {
    const queryParams: any = {
      practiceId: params.practiceId,
      locationId: params.locationId,
      providerId: params.providerId
    };

    if (params.fullName) {
      queryParams.fullName = params.fullName;
    }

    if (params.dob) {
      queryParams.dob = params.dob; // Format: yyyy-mm-dd
    }

    return this.apiService.get<any>('patients/search', { params: queryParams });
  }

  private static readonly GET_PATIENT_LEDGER_QUERY = `
query GetPatientLedger($input: GetPatientLedgerInput!) {
  getPatientLedger(input: $input) {
    message
    entries {
      pkId
      patientVisitId
      visitChargeEntryId
      patientDate
      dosDate
      entryDate
      datePaid
      planName
      description
      fee
      planAmt
      patientAmt
      planBal
      patientBal
      comments
      entryType
      ledgerType
      planDescription
      provider
      location
      userName
      chargeIsTransferred
      claimStatus
      statementId
      rowColorKey
      rowColorHex
      nextAction
    }
    summary {
      planAmt
      patientAmt
      planPayment
      copayment
      patientPayment
      writeOff
      advancePayment
      discount
      cash
      creditMemo
      check
      creditCard
      planBalance
      patientBalance
    }
  }
}
`;

  /**
   * GraphQL: getPatientLedger – entries and summary for Patient Ledger popup.
   * Call when popup loads or user clicks Search; map entries to table and summary to form.
   */
  getPatientLedgerGraphQL(params: {
    patientAccountNo: number | string;
    dosFrom: string;
    dosTo: string;
  }): Observable<{ getPatientLedger: GetPatientLedgerGqlResult }> {
    const variables = {
      input: {
        patientAccountNo: Number(params.patientAccountNo),
        dosFrom: String(params.dosFrom),
        dosTo: String(params.dosTo)
      }
    };
    // Return raw GraphQL data shape: { getPatientLedger: { message, entries, summary } }
    return this.apiService.graphql<{ getPatientLedger: GetPatientLedgerGqlResult }>(
      PatientService.GET_PATIENT_LEDGER_QUERY,
      variables
    );
  }

  /**
   * Single ledger entry from GET /patients/ledger/{ledgerId} (Delphi tblLedger row).
   */
  getLedger(ledgerId: number): Observable<LedgerDetailApiResponse> {
    return this.apiService.get<any>(`patients/ledger/${ledgerId}`).pipe(
      map((response) => ({
        ledger:
          response?.data?.ledger ??
          response?.ledger ??
          null,
        message:
          response?.data?.message ??
          response?.message
      }))
    );
  }

  updateLedger(ledgerId: number, payload: UpdateLedgerRequest): Observable<{ status?: string; message?: string; data?: unknown }> {
    return this.apiService.put<any>(`patients/ledger/${ledgerId}`, payload).pipe(
      map((response) => ({
        status: response?.status,
        message: response?.message,
        data: response?.data
      }))
    );
  }

  /**
   * GET /patients/{patientAccountNo}/history
   * REST wrapper for patient history.
   * - type omitted or 'appointment' => demographics + scheduled + past appointment history
   * - type 'patient' | 'plan' | 'statement' => change history for corresponding History tab
   * Used by History popup on Edit Patient and other screens.
   */
  getPatientHistoryRest(
    patientAccountNo: number,
    page: number = 1,
    limit: number = 50,
    type?: 'appointment' | 'patient' | 'plan' | 'statement'
  ): Observable<any> {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(limit)
    };
    if (type && type.trim() !== '') {
      params['type'] = type;
    }

    return this.apiService.get<any>(`patients/${patientAccountNo}/history`, {
      params
    });
  }

  /**
   * List ledger entries for Patient Ledger table.
   * GET /patients/ledger?accountNo=...&patientVisitId=...&dosFrom=...&dosTo=...
   * Backend expects accountNo (not patientAccountNo) to avoid "Invalid patient account number".
   */
  getPatientLedgerList(params: GetPatientLedgerListParams): Observable<PatientLedgerListApiResponse> {
    const patientAccountNo = params['patientAccountNo'];
    const patientVisitId = params['patientVisitId'];
    const dosFrom = params['dosFrom'];
    const dosTo = params['dosTo'];
    const includeCharges = params['includeCharges'];
    const includeLedger = params['includeLedger'];
    const includeSubmit = params['includeSubmit'];
    const includeStatement = params['includeStatement'];
    const queryParams: Record<string, string> = {
      accountNo: String(patientAccountNo)
    };
    if (patientVisitId != null && patientVisitId !== '') {
      queryParams['patientVisitId'] = String(patientVisitId);
    }
    if (dosFrom) queryParams['dosFrom'] = dosFrom;
    if (dosTo) queryParams['dosTo'] = dosTo;
    if (includeCharges != null) queryParams['includeCharges'] = String(includeCharges);
    if (includeLedger != null) queryParams['includeLedger'] = String(includeLedger);
    if (includeSubmit != null) queryParams['includeSubmit'] = String(includeSubmit);
    if (includeStatement != null) queryParams['includeStatement'] = String(includeStatement);
    return this.apiService.get<PatientLedgerListApiResponse>('patients/ledger', { params: queryParams });
  }

  /**
   * GET /patients/eligibility – list eligibility records with optional filters and pagination.
   */
  getEligibilityList(params: GetEligibilityListParams): Observable<ListEligibilityResponse> {
    const queryParams: Record<string, string | number> = {};
    if (params.patientAccountNo != null && params.patientAccountNo !== '') {
      queryParams['patientAccountNo'] = String(params.patientAccountNo);
    }
    if (params.patientName != null && params.patientName !== '') queryParams['patientName'] = params.patientName;
    if (params.practice != null && params.practice !== '') queryParams['practice'] = params.practice;
    if (params.location != null && params.location !== '') queryParams['location'] = params.location;
    if (params.serviceDateFrom != null && params.serviceDateFrom !== '') queryParams['serviceDateFrom'] = params.serviceDateFrom;
    if (params.serviceDateTo != null && params.serviceDateTo !== '') queryParams['serviceDateTo'] = params.serviceDateTo;
    if (params.eligibilityDateFrom != null && params.eligibilityDateFrom !== '') queryParams['eligibilityDateFrom'] = params.eligibilityDateFrom;
    if (params.eligibilityDateTo != null && params.eligibilityDateTo !== '') queryParams['eligibilityDateTo'] = params.eligibilityDateTo;
    if (params.serviceTypeCode != null && params.serviceTypeCode !== '') queryParams['serviceTypeCode'] = params.serviceTypeCode;
    if (params.planName != null && params.planName !== '') queryParams['planName'] = params.planName;
    if (params.response != null && params.response !== '') queryParams['response'] = params.response;
    if (params.page != null) queryParams['page'] = params.page;
    if (params.limit != null) queryParams['limit'] = params.limit;
    return this.apiService.get<ListEligibilityResponse>('patients/eligibility', { params: queryParams });
  }

  /**
   * POST /patients/eligibility – add eligibility record.
   */
  postEligibility(body: AddEligibilityRequest): Observable<AddEligibilityResponse> {
    return this.apiService.post<AddEligibilityResponse>('patients/eligibility', body);
  }

  /**
   * POST /eligibility/context
   * Fetch eligibility context used to auto-populate Add/Edit Eligibility popup.
   */
  getEligibilityContext(body: EligibilityContextRequest): Observable<EligibilityContextApiResponse> {
    const payload: EligibilityContextRequest = {
      patientAccountNo: Number(body.patientAccountNo)
    };
    if (body.serviceDate != null && String(body.serviceDate).trim() !== '') {
      payload.serviceDate = String(body.serviceDate).trim();
    }
    return this.apiService.post<EligibilityContextApiResponse>('eligibility/context', payload);
  }

  /**
   * POST /eligibility/screen-details
   * Fetch eligibility screen details used by Add/Edit Eligibility modal (payer/provider/subscriber + validations).
   */
  getEligibilityScreenDetails(body: EligibilityScreenDetailsRequest): Observable<EligibilityScreenDetailsApiResponse> {
    const payload: EligibilityScreenDetailsRequest = {
      patientAccountNo: Number(body.patientAccountNo),
      eligibilityId: Number(body.eligibilityId)
    };
    return this.apiService.post<EligibilityScreenDetailsApiResponse>('eligibility/screen-details', payload);
  }

  /**
   * Get Patient Summary using GraphQL
   */
  getPatientSummary(patientAccountNo: number): Observable<any> {
    if (!patientAccountNo || isNaN(patientAccountNo)) {
      return throwError(() => new Error('Invalid patient account number.'));
    }

    const cached = this.patientSummaryCache.get(patientAccountNo);
    if (cached) {
      return cached;
    }

    const query = `
      query GetPatientSummary($accountNo: Int!) {
      getPatientSummary(input: { patientAccountNo: $accountNo }) {
        message
        patientAccountNo
          detail {
          patientName
          accountNo
          dob
          address
          city
          state
          zipCode
          homeTel
          workTel
          workPhoneExt
          advancePaid
          lastPayDate
          lastPayAmount
          statementDate
          statementAmount
          comments
          parentPatientAccountNo
        }
          financialBalance {
          planTotalBalance
          planCurrent
          plan30Plus
          plan60Plus
          plan90Plus
          plan120Plus
          patientTotalBalance
          patientCurrent
          patient30Plus
          patient60Plus
          patient90Plus
          patient120Plus
        }
          lastVisit {
          dos
          practice
          location
          provider
          plan
          planAmt
          patAmt
          patientVisitId
        }
          scheduledAppointments {
          appointmentDate
          dayName
          providerShortName
          reasonShortName
          patientAppointmentId
        }
          patientPlans {
          planName
          planPriority
          planType
        }
      }
    }
    `;

    const variables = {
      accountNo: patientAccountNo
    };

    const request$ = this.apiService.graphql<any>(query, variables).pipe(
      shareReplay(1),
      catchError((error) => {
        this.patientSummaryCache.delete(patientAccountNo);
        return throwError(() => error);
      })
    );

    this.patientSummaryCache.set(patientAccountNo, request$);
    return request$;
  }

  prefetchPatientSummary(patientAccountNo: number): void {
    this.getPatientSummary(patientAccountNo).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  clearPatientSummaryCache(patientAccountNo?: number): void {
    if (patientAccountNo != null) {
      this.patientSummaryCache.delete(patientAccountNo);
      return;
    }
    this.patientSummaryCache.clear();
  }
}
